import React, { useState } from "react";
import {
  Button,
  Popover,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  DialogContentText,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { useEncryptService } from "../../../shared/hooks";
import { toast } from "react-toastify";
import { ROLES_CONTENT_PROPS } from "../../../shared/constants/constant-data";
import useRolePermissions from "../../../shared/hooks/useRolePermissions";

const VpnActions = ({
  vpnDetails,
  handlePauseVpn,
  handleResumeVpn,
  handleDeleteVpn,
  callBaseVPN,
}: any) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [dialogType, setDialogType] = useState<
    "edit" | "pause" | "delete" | "resume" | null
  >(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false);
  const { hasPermissions } = useRolePermissions();
  const [editDetails, setEditDetails] = useState({
    name: vpnDetails.tunnel_name,
    description: vpnDetails.tunnel_description,
    customer_gw_ip: vpnDetails.client_gateway_ip,
    customer_cidr: vpnDetails.client_cidr_block,
  });
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);

  const lastErrorMessageRef = React.useRef<string | null>(null);
  const toastTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const toastDuration = 5000;

  const open = Boolean(anchorEl);
  const { doEncryptLogin } = useEncryptService();

  const handleActionsNow = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (type: "edit" | "pause" | "resume" | "delete") => {
    setDialogType(type);
    handleClosePopover();
  };

  const handleCloseDialog = () => {
    setDialogType(null);
    setPassword("");
  };

  const handleEditVpn = async (payload: any) => {
    setLoading(true);
    setIsButtonDisabled(true);
    const req_obj = {
      name: payload.name,
      description: payload.description,
      customer_gw_ip: payload.customer_gw_ip,
      customer_cidr: payload.customer_cidr,
      customer_private_ip: payload.customer_private_ip,
      tunnel_id: payload.tunnel_id,
    };
    try {
      const response = await axiosInstance.put(END_POINTS.VPN, req_obj);
      if (response.status === 200) {
        toast(response.data.message, { type: "success" });
        if (typeof callBaseVPN === 'function') {
          await callBaseVPN();
        }
        setDialogType(null);
        setLoading(false);
        setIsButtonDisabled(false);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Something went wrong";
      toast.error(errorMessage, { type: "error" });
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, toastDuration);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: vpnDetails.tunnel_name || "",
      description: vpnDetails.tunnel_description || "",
      customer_gw_ip: vpnDetails.client_gateway_ip || "",
      customer_cidr: vpnDetails.client_cidr_block || "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Tunnel Name is required")
        .min(3, "Minimum 3 characters")
        .max(20, "Maximum 20 characters"),
      description: Yup.string()
        .required("Tunnel Description is required")
        .min(3, "Minimum 3 characters")
        .max(50, "Maximum 50 characters"),
      customer_gw_ip: Yup.string()
        .required("Customer Gateway IP is required")
        .min(3, "Minimum 3 characters")
        .max(50, "Maximum 50 characters"),
      customer_cidr: Yup.string()
        .required("Customer CIDR Block is required")
        .min(3, "Minimum 3 characters")
        .max(50, "Maximum 50 characters"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        ...values,
        customer_private_ip: values.customer_gw_ip,
        tunnel_id: vpnDetails.tunnel_id,
      };
      await handleEditVpn(payload);
      resetForm();
    },
  });

  const handlePause = async () => {
    const payload = {
      tunnel_id: vpnDetails.tunnel_id,
      pause: true,
    };
    console.log(payload);
    handlePauseVpn(payload);
    setDialogType(null);

    //api here
  };

  const handleResume = async () => {
    const payload = {
      tunnel_id: vpnDetails.tunnel_id,
      pause: false,
    };
    console.log(payload);
    handleResumeVpn(payload);
    setDialogType(null);

    //api here
  };

  const handleDelete = async (password: any) => {
    console.log(password, "delete payload");
    const password_encrypt = {
      password: doEncryptLogin(password),
    };
    setLoading(true);
    try {
      const response = await axiosInstance.post(
        END_POINTS.PASSWORD_CONFIRMATION,
        password_encrypt
      );
      if (response.status === 200) {
        // toast(response.data.message, { type: "success" });
        handleDeleteVpn(vpnDetails.tunnel_id);
        setDialogType(null);
      }
    } catch (error: any) {
      console.error("Error deleting VPN:", error);
      const errorMessage =
        error?.response?.data?.message || "Something went wrong";
      if (
        lastErrorMessageRef.current !== errorMessage ||
        !toastTimeoutRef.current
      ) {
        toast(errorMessage, { type: "error" });
        lastErrorMessageRef.current = errorMessage;
        if (toastTimeoutRef.current) {
          clearTimeout(toastTimeoutRef.current);
        }
        toastTimeoutRef.current = setTimeout(() => {
          toastTimeoutRef.current = null;
          lastErrorMessageRef.current = null;
        }, toastDuration);
      }
      setIsButtonDisabled(false);
    } finally {
      setIsButtonDisabled(false);
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        className="btn btn-outline"
        onClick={handleActionsNow}
        sx={{
          "&:focus": { outline: "none" },
        }}
      >
        Action
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {hasPermissions(ROLES_CONTENT_PROPS.CREATE_VPN) && (
          <Typography
            sx={{
              padding: "10px",
              cursor: "pointer",
              color: "#8784AA",
              width: "80px",
              "&:hover": {
                color: "#3A2ADA",
                backgroundColor: "#F0F0FF",
                borderRadius: "4px",
              },
            }}
            onClick={() => handleOpenDialog("edit")}
          >
            Edit
          </Typography>
        )}
        {hasPermissions(ROLES_CONTENT_PROPS.CREATE_VPN) &&
          !vpnDetails.paused &&
          vpnDetails.tunnel_status !== "Failed" &&
          vpnDetails.tunnel_status !== "Inactive" && (
            <Typography
              sx={{
                padding: "10px",
                cursor: "pointer",
                color: "#8784AA",
                "&:hover": {
                  color: "#3A2ADA",
                  backgroundColor: "#F0F0FF",
                  borderRadius: "4px",
                },
              }}
              onClick={() => handleOpenDialog("pause")}
            >
              Pause
            </Typography>
          )}

        {hasPermissions(ROLES_CONTENT_PROPS.CREATE_VPN) &&
          vpnDetails.paused && (
            <Typography
              sx={{
                padding: "10px",
                cursor: "pointer",
                color: "#8784AA",
                "&:hover": {
                  color: "#3A2ADA",
                  backgroundColor: "#F0F0FF",
                  borderRadius: "4px",
                },
              }}
              onClick={() => handleOpenDialog("resume")}
            >
              Resume
            </Typography>
          )}
        {hasPermissions(ROLES_CONTENT_PROPS.DELETE_VPN) && (
          <Typography
            sx={{
              padding: "10px",
              cursor: "pointer",
              color: "#8784AA",
              "&:hover": {
                color: "#3A2ADA",
                backgroundColor: "#F0F0FF",
                borderRadius: "4px",
              },
            }}
            onClick={() => handleOpenDialog("delete")}
          >
            Delete
          </Typography>
        )}
      </Popover>

      {/* Edit Dialog */}
      {dialogType === "edit" && (
        <Dialog
          sx={{
            "& .MuiDialog-paper": {
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "350px",
              height: "470px",
              transform: "translate(-50%, -50%)",
            },
          }}
          open={true}
          onClose={handleCloseDialog}
        >
          <DialogTitle>Edit VPN</DialogTitle>
          <form onSubmit={formik.handleSubmit}>
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center", // Centers the text fields vertically
                flexGrow: 1, // Ensures the content takes up available space
                gap: "20px", // Adds spacing between text fields
              }}
            >
              <TextField
                label="Tunnel Name"
                fullWidth
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                // helperText={formik.touched.name && formik.errors.name}
                sx={{ marginBottom: "10px" }}
              />
              <TextField
                label="Tunnel Description"
                fullWidth
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                //helperText={formik.touched.description && formik.errors.description}
                sx={{ marginBottom: "10px" }}
              />
              <TextField
                label="Customer Gateway IP"
                fullWidth
                name="customer_gw_ip"
                value={formik.values.customer_gw_ip}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.customer_gw_ip &&
                  Boolean(formik.errors.customer_gw_ip)
                }
                //helperText={formik.touched.customer_gw_ip && formik.errors.customer_gw_ip}
                sx={{ marginBottom: "10px" }}
              />
              <TextField
                label="Customer CIDR Block"
                fullWidth
                name="customer_cidr"
                value={formik.values.customer_cidr}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.customer_cidr &&
                  Boolean(formik.errors.customer_cidr)
                }
              />
            </DialogContent>
            <DialogActions
              sx={{
                display: "flex",
                justifyContent: "center", // Centers the buttons horizontally
                gap: "10px", // Adds spacing between buttons
                padding: "20px", // Adds padding at the bottom
              }}
            >
              <Button
                sx={{
                  width: "120px",
                  color: "#696595",
                  borderColor: "#696595",
                }}
                variant="outlined"
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn-primary"
                variant="contained"
                sx={{ width: "120px", height: "33px" }}
                disabled={!formik.isValid || !formik.dirty || loading || isButtonDisabled}
                endIcon={loading ? <CircularProgress size={15} color="inherit" /> : null}
              >
                Save
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}

      {/* Pause Dialog */}
      {dialogType === "pause" && (
        <Dialog
          sx={{
            "& .MuiDialog-paper": {
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "360px",
              height: "210px",
              transform: "translate(-50%, -50%)",
            },
          }}
          open={true}
          onClose={handleCloseDialog}
        >
          <DialogTitle>
            <p className="dialog-title">Are you sure want to Pause</p>
            <p className="dialog-title">"{vpnDetails.tunnel_name}"</p>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ marginTop: "10px" }}>
              <p className="dialog-content">
                You won't be able to connect using this
              </p>
              <p className="dialog-content">tunnel. You can resume it later.</p>
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              marginBottom: "15px",
            }}
          >
            <Button
              sx={{ width: "120px", color: "#696595", borderColor: "#696595" }}
              variant="outlined"
              onClick={handleCloseDialog}
            >
              Cancel
            </Button>
            <Button
              sx={{ width: "120px", background: "#F7941F", color: "white" }}
              onClick={handlePause}
            >
              Pause
            </Button>
          </DialogActions>
        </Dialog>
        // <Dialog open={true} onClose={handleCloseDialog}>
        //   <DialogTitle>Are you sure you want to pause "{vpnDetails.tunnel_name}"?</DialogTitle>
        //   <DialogContent>
        //     <Typography>
        //       You won't be able to connect using this tunnel. You can resume it later.
        //     </Typography>
        //   </DialogContent>
        //   <DialogActions>
        //     <Button onClick={handleCloseDialog}>Cancel</Button>
        //     <Button variant="contained" color="warning">
        //       Pause
        //     </Button>
        //   </DialogActions>
        // </Dialog>
      )}
      {/* Pause Dialog */}
      {dialogType === "resume" && (
        <Dialog
          sx={{
            "& .MuiDialog-paper": {
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "360px",
              height: "160px",
              transform: "translate(-50%, -50%)",
            },
          }}
          open={true}
          onClose={handleCloseDialog}
        >
          <DialogTitle>
            <p className="dialog-title">Are you sure you want to Resume</p>
            <p className="dialog-title">"{vpnDetails.tunnel_name}"</p>
          </DialogTitle>
          <DialogContent></DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              marginBottom: "15px",
            }}
          >
            <Button
              sx={{ width: "120px", color: "#696595", borderColor: "#696595" }}
              variant="outlined"
              onClick={handleCloseDialog}
            >
              Cancel
            </Button>
            <Button
              sx={{ width: "120px" }}
              variant="contained"
              onClick={handleResume}
            >
              Resume
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Dialog */}
      {dialogType === "delete" && (
        // <Dialog open={true} onClose={handleCloseDialog}>
        //   <DialogTitle>Are you sure you want to delete "{vpnDetails.tunnel_name}"?</DialogTitle>
        //   <DialogContent>
        //     <Typography>
        //       If you delete, you will not be able to find the VPN again.
        //     </Typography>
        //     <TextField
        //       label="Password"
        //       type="password"
        //       fullWidth
        //       value={password}
        //       onChange={(e) => setPassword(e.target.value)}
        //       sx={{ marginTop: "10px" }}
        //     />
        //   </DialogContent>
        //   <DialogActions>
        //     <Button onClick={handleCloseDialog}>Cancel</Button>
        //     <Button variant="contained" color="error">
        //       Delete
        //     </Button>
        //   </DialogActions>
        // </Dialog>
        <Dialog
          sx={{
            "& .MuiDialog-paper": {
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "360px",
              height: "250px",
              transform: "translate(-50%, -50%)",
            },
          }}
          open={true}
          onClose={handleCloseDialog}
        >
          <DialogTitle>
            <p className="dialog-title">Are you sure you want to delete</p>
            <p style={{ marginTop: "5px" }} className="dialog-title">
              " {vpnDetails.tunnel_name}"?
            </p>
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={password}
              onChange={(e: any) => setPassword(e.target.value)}
              error={!!passwordError}
              helperText={passwordError}
              margin="normal"
              sx={{
                "& .MuiOutlinedInput-root": {
                  ...(!showPassword && { font: "initial" }),
                },
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <span className="visibility-icon">
                        {showPassword ? (
                          <Visibility
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        ) : (
                          <VisibilityOff
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        )}
                      </span>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              marginBottom: "15px",
            }}
          >
            <Button
              sx={{ width: "120px", color: "#696595", borderColor: "#696595" }}
              variant="outlined"
              onClick={handleCloseDialog}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(password)}
              sx={{ width: "120px", background: "#FB4B50", color: "white" }}
              disabled={
              !password || loading || isButtonDisabled
              }
              endIcon={
              loading ? <CircularProgress size={15} color="inherit" /> : null
              }
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default VpnActions;
